import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ClipboardCheck, ExternalLink, Unlink } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type DecisionSheetView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { useConfirmPopover } from '../../../components/ui/confirm-popover';
import { errorMessage } from '../../../lib/error-message';
import { cn } from '../../../lib/utils';
import { decisionSheetApi, decisionSheetKeys } from '../api';

/**
 * Việc đang nhúng sổ — trên trang sổ, APM-DSX-d (plan §11.2): mã, tên, người
 * được giao, trạng thái, mở việc; bỏ gắn khi `can.assignTask`.
 *
 * Rỗng ⇒ không hiện gì: phần lớn sổ không giao cho ai qua task.
 *
 * ⚠️ Không nhận `asOf` — liệt kê quan hệ **đang có** giữa sổ và việc, không có
 * trục thời gian để tua (cùng loại với `TaskBugLinks`). Đang xem quá khứ thì
 * khối ẩn hẳn thay vì đặt danh sách hiện tại cạnh một sổ của ngày cũ.
 */
export function DecisionSheetTaskList({
  view,
  historical,
}: {
  readonly view: DecisionSheetView;
  readonly historical: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const confirm = useConfirmPopover();
  const unlink = useMutation({
    mutationFn: (taskId: string) => decisionSheetApi.unlinkTask(view.id, taskId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['decision-sheet', view.id] });
      void queryClient.invalidateQueries({ queryKey: decisionSheetKeys.list(view.projectId) });
      toast.success(t('decisionSheet.tasks.unlinked'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const tasks = view.tasks ?? [];
  if (historical || tasks.length === 0) return null;
  const canUnlink = view.can.assignTask === true;

  return (
    <section className="flex flex-col gap-1.5" data-testid="decision-sheet-tasks">
      <h3 className="flex items-center gap-1.5 text-sm font-medium">
        <ClipboardCheck aria-hidden className="size-4 text-muted-foreground" />
        {t('decisionSheet.tasks.title')}
      </h3>
      <ul className="flex flex-col divide-y divide-border rounded-xl border border-border">
        {tasks.map((task) => (
          <li
            key={task.taskId}
            className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 text-sm"
            data-testid={`decision-sheet-task-${task.code}`}
          >
            <span className="font-mono text-xs text-muted-foreground">{task.code}</span>
            <span className="min-w-0 flex-1 basis-40 [overflow-wrap:anywhere]">{task.title}</span>
            <span className="text-xs text-muted-foreground">
              {task.assignee?.name ?? t('decisionSheet.tasks.unassigned')}
            </span>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-xs',
                task.done ? 'bg-rag-green/15 text-rag-green' : 'bg-muted text-muted-foreground',
              )}
            >
              {task.status}
            </span>
            <Link
              to="/projects"
              search={{ projectId: view.projectId, tab: 'tasks', taskId: task.taskId }}
              aria-label={t('decisionSheet.tasks.open', { code: task.code })}
              title={t('decisionSheet.tasks.open', { code: task.code })}
              className="inline-flex size-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <ExternalLink aria-hidden className="size-4" />
            </Link>
            {canUnlink ? (
              <Button
                size="icon"
                variant="ghost"
                className="size-8"
                aria-label={t('decisionSheet.tasks.unlink', { code: task.code })}
                title={t('decisionSheet.tasks.unlink', { code: task.code })}
                disabled={unlink.isPending}
                onClick={() =>
                  confirm.ask({
                    message: t('decisionSheet.tasks.unlinkConfirm', { code: task.code }),
                    confirmLabel: t('decisionSheet.tasks.unlinkLabel'),
                    onConfirm: () => unlink.mutate(task.taskId),
                  })
                }
              >
                <Unlink aria-hidden className="size-4" />
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {confirm.popover}
    </section>
  );
}
